/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, CircularProgress, OutlinedInput } from '@mui/material';

import { AlignCenter } from '../../../../components/AlignCenter/AlignCenter';
import { HUModal2 } from '../../../../components/HUModal2/HUModal2';
import { SimpleNotifyModal } from '../../../../components/SimpleNotifyModal/SimpleNotifyModal';
import { Namespaces } from '../../../../i18n';
import { OrganizationApiService } from '../../../../services/OrganizationApiService';
import { ApiClientError } from '../../../../types/api/ApiClientError';
import { EntityId } from '../../../../types/api/EntityId';
import { DataState } from '../../../../types/common/DataState';
import { dataError, dataLoaded, dataLoading, isDataLoading } from '../../../../utils/dataState';
import { reactToInviteUserToOrganization } from '../../../../utils/reactions';
import { useApiValidation } from '../../../../utils/useApiValidation';
import { useObjectState } from '../../../../utils/useObjectState';
import { useInviteMemberStyles } from './InviteMember.style';

type InviteMemberProps = {
  organizationId: EntityId;
  text: string;
};

type State = {
  isOpenModal: boolean;
  inviteEmail: string;
  dataState: DataState;
  errorMessage?: string;
};

export function InviteMember(props: InviteMemberProps) {
  const { organizationId, text } = props;
  const { t } = useTranslation(Namespaces.OrganizationAdmin);
  const [state, setState] = useObjectState<State>({
    isOpenModal: false,
    inviteEmail: '',
    dataState: DataState.notAsked,
  });
  const classes = useInviteMemberStyles();
  const { isOpenModal, inviteEmail } = state;
  const [isOpenErrorModal, setIsOpenErrorModal] = useState<boolean>(false);
  const { errorMessage, setApiError } = useApiValidation();

  const setInviteEmail = useCallback(
    (email: string) => {
      setState({ inviteEmail: email });
    },
    [setState],
  );

  const canInvite = useMemo(() => !!inviteEmail, [inviteEmail]);

  const onAddButtonClick = useCallback(() => {
    setState({
      dataState: dataLoading(),
      errorMessage: undefined,
    });
    const inviteUser = async () => {
      try {
        const data = {
          email: inviteEmail,
        };
        await OrganizationApiService.inviteUserToOrganization(organizationId, data);
        await reactToInviteUserToOrganization({ organizationId });
        setState({
          isOpenModal: false,
          dataState: dataLoaded(),
          inviteEmail: '',
        });
      } catch (e) {
        const error = e as ApiClientError;
        const responseData = error.response?.data;
        if (!responseData) {
          setState({ dataState: dataError(), errorMessage: undefined });
        } else {
          const errorMessage = responseData.message;
          setState({ dataState: dataError(), errorMessage });
        }
        if (error.response) {
          setApiError(error);
        }
        setIsOpenErrorModal(true);
      }
    };
    void inviteUser();
  }, [setState, inviteEmail, organizationId, setApiError]);

  const onCancel = () => {
    setState({
      isOpenModal: false,
      inviteEmail: '',
    });
  };

  const isLoading = useMemo(() => isDataLoading(state.dataState), [state.dataState]);

  return (
    <>
      <Button
        onClick={() => setState({ isOpenModal: true })}
        className={classes.createButton}
        data-cy="members-tab-add-members-button"
      >
        {text}
      </Button>

      <HUModal2
        open={isOpenModal}
        onClose={onCancel}
        size="big"
        title={t('management.members_list.modal_title')}
        footer={({ footerClass }) => (
          <div className={footerClass}>
            {canInvite ? (
              <Button color="primary" variant="contained" onClick={onAddButtonClick}>
                {t('users.action_add.action_label', {
                  count: 1,
                })}
              </Button>
            ) : (
              <Button color="secondary" variant="contained" disabled>
                {t('users.action_add.form_action_Submit')}
              </Button>
            )}
            <Button color="primary" variant="text" onClick={onCancel}>
              {t(`${Namespaces.Common}:label.cancel`)}
            </Button>
          </div>
        )}
      >
        <div>
          {isLoading && (
            <AlignCenter>
              <CircularProgress color="primary" size={40} />
            </AlignCenter>
          )}
          <OutlinedInput
            value={inviteEmail}
            className={classes.textField}
            onChange={e => setInviteEmail(e.target.value)}
            onBlur={e => setInviteEmail(e.target.value)}
            placeholder={t('users.action_add.invite_by_email')}
          />
        </div>
      </HUModal2>
      <SimpleNotifyModal
        open={isOpenErrorModal}
        message={t([
          `${Namespaces.OrganizationAdmin}:users.action_add.${errorMessage}`,
          `${Namespaces.Common}:errors.unexpected_error`,
        ])}
        onOk={() => setIsOpenErrorModal(false)}
      />
    </>
  );
}
